import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import {
    Button,
    Col,
    Drawer,
    Form,
    Grid,
    List,
    notification,
    Row,
    Spin,
    Tabs,
    Typography,
} from 'antd';
import axios from 'axios';
import { getFileName, getLangValueParams, getValueFromLanguageSet, localize } from 'mixins/helpers';
import React, { useEffect, useState } from 'react';
import 'react4xp/parts/service-unreliable-taxpayers/service-unreliable-taxpayers.scss';
import { IDGDitem } from './interfaces/IDGDitem';
import { ICategory } from '/react4xp/parts/manage-reestr-list/manage-reestr-list.type';
import {
    documentHistoryCategories,
    getTaxOrg,
} from '/react4xp/parts/service-unreliable-taxpayers/actions';

const { useBreakpoint } = Grid;

const DOC_CATS_URL = `/services/isnaportal/open-api/admin/document-history/categories`;
const DOWNLOAD_URL = `/services/isnaportal/open-api/taxpayer-data/export`;
const TAX_ORG_SEARCH = `/services/isnaportal/open-api/tax-org/search`;
const DOWNLOAD_URL_EXCEL = `/services/isnaportal/open-api/taxpayer-data/export/excel`;

const DOCUMENT_CATEGORIES_WITH_EXCEL = [
    'REREGISTRATION_INVALID',
    'REGISTRATION_INVALID',
    'INACTIVE',
    'BANKRUPT',
];

const documentCategorySortOrder = {
    BANKRUPT: 1,
    INACTIVE: 3,
    REGISTRATION_INVALID: 4,
    REREGISTRATION_INVALID: 5,
    ABSENT_ADDRESS: 6,
};

const dgdSortOrder = {
    '6201': 19,
    '6001': 20,
    '5901': 21,
};

const NOT_RELATED_DOCUMENT_CATEGORIES = [
    'REGISTER',
    'ADMITTED_FOR_EXAM',
    'LIST_OF_ADMITTED_EXAM_QUESTIONS',
    'PSEUDO_ENTERPRISE',
    'IP_LZCHP_TAX_OVERDUE',
];

const CATEGORY_NAME_OVERRIDES: Partial<Record<string, ICategory['name']>> = {
    AGENT_TAX_OVERDUE: {
        nameRu:
            'Информация о налогоплательщиках - юридических лицах, их структурных подразделениях, имеющих налоговую задолженность более 20 МРП, не погашенную в течение 4 месяцев со дня ее возникновения',
        nameKk:
            '20 АЕК-тен астам салық берешегі бар, олар пайда болған күннен бастап 4 ай ішінде өтемеген салық төлеушілер – заңды тұлғалар, олардың құрылымдық бөлімшелері туралы мәліметтер',
        nameQq:
            '20 AEK-ten astam salyq bereshegi bar, olar paıda bolǵan kúnnen bastap 4 aı ishinde ótemegen salyq tóleýshiler – zańdy tulǵalar, olardyń qurylymdyq bólimsheleri týraly málimetter',
        nameEn:
            'Information about taxpayers - legal entities, their structural subdivisions, having a tax debt of more than 20 MCI, not repaid within 4 months from the date of its occurrence',
    },
};

type TabPosition = 'left' | 'right' | 'top' | 'bottom';

const ListForm = (props: any) => {
    const [form] = Form.useForm();
    const [tabPosition, setTabPosition] = useState<TabPosition>('left');
    const [dgdData, setDgdData] = useState<IDGDitem[]>();
    const [categoryData, setCategoryData] = useState<ICategory[]>([]);
    const [isDownloading, setIsDownloading] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [activeCategory, setActiveCategory] = useState<ICategory>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(true);

    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const loadData = () => {
        setIsLoading(true);

        getTaxOrg()
            .then((results) => {
                const dgd_items = results.data.sort((a: IDGDitem, b: IDGDitem) => {
                    const orderA = dgdSortOrder[a.code] || Number.MAX_SAFE_INTEGER;
                    const orderB = dgdSortOrder[b.code] || Number.MAX_SAFE_INTEGER;
                    return orderB - orderA;
                });

                setDgdData(dgd_items);
            })
            .finally(() => setIsLoading(false));

        documentHistoryCategories().then((result) => {
             const tab_items = result.data.map((item) => {
                const nameOverride = CATEGORY_NAME_OVERRIDES[item.documentCategory];

                if (!nameOverride) {
                    return item;
                }

                return {
                    ...item,
                    name: {
                        ...item.name,
                        ...nameOverride,
                    },
                };
            });

            const filtered_tab_items = tab_items
                .filter((item) => !NOT_RELATED_DOCUMENT_CATEGORIES.includes(item.documentCategory))
                .sort((a: any, b: any) => {
                    const orderA =
                        documentCategorySortOrder[a.documentCategory] || Number.MAX_SAFE_INTEGER;
                    const orderB =
                        documentCategorySortOrder[b.documentCategory] || Number.MAX_SAFE_INTEGER;

                    return orderA - orderB;
                });

            const specialCategory: ICategory = {
                id: 9999,
                createdDate: null,
                name: {
                    nameRu: 'Список налогоплательщиков, которым приостановлена выписка ЭСФ',
                    nameKk: 'ESF-ті тоқтатқан салық төлеушілер тізімі',
                    nameQq: 'ESF-ті toqtatqan salyq tölewshiler tizimi',
                    nameEn: 'List of taxpayers with suspended ESF issuance',
                },
                documentCategory: 'SUSPENDED_ESF',
                orderNumber: 0,
            };

            const allCategories = [...filtered_tab_items, specialCategory];

            setCategoryData(allCategories);
            setActiveCategory(allCategories[0]);
        });
    };

    useEffect(() => {
        loadData();
    }, []);

    const downloadSuspendedEsf = async () => {
        setIsDownloading('SUSPENDED_ESF');

        axios({
            url: '/services/isnaportal/open-api/esf-registration/export/excel',
            params: {
                language: props.language === 'en_US' ? 'eng' : 'rus',
            },
            method: 'GET',
            responseType: 'blob',
        })
            .then((response) => {
                const filename =
                    getFileName(response.headers['content-disposition']) ||
                    `List_of_taxpayers_with_suspended_ESF_issuance.xlsx`;
                const url = URL.createObjectURL(response.data);

                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                link.click();
            })
            .catch((error) => {
                notification.error({
                    message: 'Ошибка при скачивании файла',
                });
            })
            .finally(() => setIsDownloading(null));
    };

    const downloadXlsFile = async (type: string, dgdCode: string) => {
        // const isVerified = await executeRecaptcha('download_xls_file');
        // if (!isVerified) {
        //     return;
        // }

        setIsDownloading(dgdCode);
        axios({
            url:
                DOCUMENT_CATEGORIES_WITH_EXCEL.indexOf(type) !== -1
                    ? DOWNLOAD_URL_EXCEL
                    : DOWNLOAD_URL,
            method: 'GET',
            responseType: 'blob',
            params: {
                language: props.language === 'en_US' ? 'eng' : getLangValueParams(props.language),
                type: type,
                taxOrgCode: dgdCode,
            },
        })
            .then((response) => {
                const filename =
                    getFileName(response.headers['content-disposition']) ||
                    `export_${type}_${dgdCode}`;
                const url = URL.createObjectURL(response.data);

                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                link.click();
            })
            .catch((error) => {
                notification['warning']({
                    message: 'По выбранному региону данные отсутствуют',
                });
            })
            .finally(() => setIsDownloading(null));
    };

    const getListItemFromType = (item: ICategory) => {

        if (item?.documentCategory === 'SUSPENDED_ESF') {
            return (
                <div style={{ padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                        Список налогоплательщиков, которым приостановлена выписка ЭСФ
                    </span>
                    <Button
                        size="large"
                        loading={isDownloading === 'SUSPENDED_ESF'}
                        onClick={downloadSuspendedEsf}
                    >
                        {localize('button.download', props?.translations)}
                    </Button>
                </div>
            );
        }

        return (
            <List
                size="large"
                dataSource={dgdData}
                renderItem={(dgd) => (
                    <List.Item style={{ margin: 0, textAlign: 'left', flexWrap: 'nowrap' }}>
                        <Typography.Text>
                            {getValueFromLanguageSet(
                                dgd,
                                props.language === 'en_US' ? 'eng' : getLangValueParams(props.language)
                            )}
                        </Typography.Text>

                        <Button
                            size="large"
                            loading={isDownloading === dgd.code}
                            onClick={() => downloadXlsFile(item.documentCategory, dgd.code)}
                        >
                            {localize('button.download', props?.translations)}
                        </Button>
                    </List.Item>
                )}
            />
        );
    };

    return (
        <div className="list-form">
            <Spin spinning={isLoading}>
                <Form
                    name="ListUnreliableTaxpayers"
                    form={form}
                    layout="vertical"
                >
                    <Row gutter={{ xs: 8, md: 20 }}>
                        <Col
                            xs={24}
                            md={24}
                        >
                            {isMobile ? (
                                <>
                                    <Button
                                        type="text"
                                        icon={<MenuUnfoldOutlined style={{ fontSize: 20 }} />}
                                        onClick={() => setIsDrawerOpen(true)}
                                        style={{ width: 46, marginLeft: 10 }}
                                    />
                                    <Drawer
                                        placement="left"
                                        closable={false}
                                        open={isDrawerOpen}
                                        onClose={() => setIsDrawerOpen(false)}
                                        getContainer={false}
                                        title={
                                            <Button
                                                type="text"
                                                icon={<MenuFoldOutlined style={{ fontSize: 20 }} />}
                                                onClick={() => setIsDrawerOpen(false)}
                                                style={{ width: 46 }}
                                            />
                                        }
                                    >
                                        <List
                                            dataSource={categoryData}
                                            renderItem={(category) => {
                                                return (
                                                    <List.Item
                                                        style={{
                                                            padding: 0,

                                                            margin: 0,
                                                            height: 'auto',
                                                        }}
                                                    >
                                                        <Button
                                                            onClick={() => {
                                                                setActiveCategory(category);
                                                                setIsDrawerOpen(false);
                                                            }}
                                                            style={{
                                                                whiteSpace: 'normal',
                                                                wordWrap: 'break-word',
                                                                height: '100%',
                                                                width: '100%',
                                                                paddingTop: 10,
                                                                paddingBottom: 10,
                                                                backgroundColor:
                                                                    category.id ===
                                                                        activeCategory?.id &&
                                                                    '#164b84',
                                                                color:
                                                                    category.id ===
                                                                    activeCategory?.id
                                                                        ? 'white'
                                                                        : 'black',
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            {getValueFromLanguageSet(
                                                                category.name,
                                                                props.language === 'en_US'
                                                                    ? 'eng'
                                                                    : getLangValueParams(
                                                                          props.language
                                                                      )
                                                            )}
                                                        </Button>
                                                    </List.Item>
                                                );
                                            }}
                                        />
                                    </Drawer>
                                    {getListItemFromType(activeCategory)}
                                </>
                            ) : (
                                <Tabs
                                    type="card"
                                    tabPosition={tabPosition}
                                    size="large"
                                    items={categoryData.map((item, i) => {
                                        const id = String(i + 1);
                                        return {
                                            label: getValueFromLanguageSet(
                                                item.name,
                                                props.language === 'en_US'
                                                    ? 'eng'
                                                    : getLangValueParams(props.language)
                                            ),
                                            key: id,
                                            children: getListItemFromType(item),
                                        };
                                    })}
                                />
                            )}
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </div>
    );
};

export default ListForm;
